import React, { useMemo, useState } from 'react'
import { useStyles } from './FlyOutArnAndDataFeedsWrapper.styles'
import {
  BoxV2,
  ButtonV2,
  ExternalLink,
  LabraToolTipV2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay } from '@fortawesome/free-solid-svg-icons'
import { CreateStackHelpFaqs } from '../CreateStackHelpFaqs/CreateStackHelpFaqs'
import { ColumnReadMore } from '@labrav/common'
import CreateStack from '../../../common/images/CreateStack.png'
import WhatAreYouDoingHereData from '../../../mocks/whatAreYouDoingHere.json'
import { Typography } from '@mui/material'
import clsx from 'clsx'
import { VideoModal } from '../../../common/components/ProductVideoAction/VideoModal'
import {
  CreateStacksStepStatusIcon,
  CreateStacksSectionActions,
} from './CreateStacksStepStatusIcon/CreateStacksStepStatusIcon'
import { TerraformStackUsageModal } from '../TerraformStackUsageModal/TerraformStackUsageModal'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { useFlagValue } from '@labrav/flags'
import {
  CLOUDFORMATION_STACK_DESCRIPTION,
  DATA_FEEDS_S3_RADIO_MAPPING,
  LOCKED_CHOICE_TOOLTIP,
  STACK_DEPLOYMENT_RADIO_MAPPING,
  TERRAFORM_DISABLE_YES_TOOLTIP,
  TERRAFORM_STACK_DESCRIPTION,
} from './constants'
import {
  normalizeDataFeedsOwnS3Choice,
  normalizeStackDeploymentType,
  parseDataFeedsOwnS3Choice,
  parseStackDeploymentType,
} from './helpers'
import type {
  DataFeedsOwnS3BucketChoice,
  FlyOutArnAndDataFeedsWrapperProps,
  StackDeploymentType,
} from './types'

export type {
  DataFeedsOwnS3BucketChoice,
  FlyOutArnAndDataFeedsWrapperProps,
  StackDeploymentType,
}

export const FlyOutArnAndDataFeedsWrapper: React.FC<
  FlyOutArnAndDataFeedsWrapperProps
> = ({
  arnChildren,
  dataFeedsChildren,
  stackType,
  onStackTypeChange,
  dataFeedsInOwnS3Bucket,
  onDataFeedsInOwnS3BucketChange,
  disabledDataFeedsInOwnS3BucketChoices = [],
  expandBox = false,
  awsAccountId = '',
  awsAccountName = '',
  clientExternalId = '',
}) => {
  const { classes } = useStyles()

  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [isTerraformUsageModalOpen, setIsTerraformUsageModalOpen] =
    useState(false)
  const [internalStackType, setInternalStackType] =
    useState<StackDeploymentType>()
  const [internalDataFeedsInOwnS3Bucket, setInternalDataFeedsInOwnS3Bucket] =
    useState<DataFeedsOwnS3BucketChoice>()
  const { value: isOnboardingImprovementsEnabled } = useFlagValue(
    'onboardingImprovements'
  )

  const stackTypeControlled =
    stackType !== undefined && onStackTypeChange !== undefined
  const currentStackType = normalizeStackDeploymentType(
    stackTypeControlled ? stackType : internalStackType
  )
  const setStackType = stackTypeControlled
    ? onStackTypeChange
    : setInternalStackType

  const dataFeedsControlled =
    dataFeedsInOwnS3Bucket !== undefined &&
    onDataFeedsInOwnS3BucketChange !== undefined
  const rawChoice = dataFeedsControlled
    ? dataFeedsInOwnS3Bucket
    : internalDataFeedsInOwnS3Bucket
  const currentDataFeedsInOwnS3Bucket = normalizeDataFeedsOwnS3Choice(rawChoice)

  const setDataFeedsInOwnS3Bucket = dataFeedsControlled
    ? onDataFeedsInOwnS3BucketChange
    : setInternalDataFeedsInOwnS3Bucket

  const effectiveDisabledDataFeedsInOwnS3BucketChoices = useMemo(() => {
    const disabled = [...disabledDataFeedsInOwnS3BucketChoices]
    if (
      isOnboardingImprovementsEnabled &&
      currentStackType === 'terraform' &&
      !disabled.includes('yes')
    ) {
      disabled.push('yes')
    }
    return disabled
  }, [
    disabledDataFeedsInOwnS3BucketChoices,
    currentStackType,
    isOnboardingImprovementsEnabled,
  ])

  const onStackTypeRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseStackDeploymentType(e.target.value)
    if (!next || next === currentStackType) {
      return
    }
    setStackType(next)
    if (currentDataFeedsInOwnS3Bucket !== 'no') {
      setDataFeedsInOwnS3Bucket('no')
    }
  }

  const stackTypeRadioCheckedValue = useMemo(
    () => ({
      key: currentStackType,
      value: currentStackType,
    }),
    [currentStackType]
  )

  const onDataFeedsRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseDataFeedsOwnS3Choice(e.target.value)
    if (!next) {
      return
    }
    if (
      next === currentDataFeedsInOwnS3Bucket ||
      effectiveDisabledDataFeedsInOwnS3BucketChoices.includes(next)
    ) {
      return
    }
    setDataFeedsInOwnS3Bucket(next)
  }

  const radioCheckedValue = useMemo(
    () => ({
      key: currentDataFeedsInOwnS3Bucket,
      value: currentDataFeedsInOwnS3Bucket,
    }),
    [currentDataFeedsInOwnS3Bucket]
  )

  const radioMappings = useMemo<RadioButtonGroupMapping[]>(
    () =>
      DATA_FEEDS_S3_RADIO_MAPPING.map(option => {
        const choice = normalizeDataFeedsOwnS3Choice(option.key)
        const isDisabledByTerraform =
          Boolean(isOnboardingImprovementsEnabled) &&
          currentStackType === 'terraform' &&
          choice === 'yes'
        const isDisabledByOnboardingLock =
          effectiveDisabledDataFeedsInOwnS3BucketChoices.includes(choice)
        const isDisabled = isDisabledByOnboardingLock || isDisabledByTerraform
        const disabledTooltip = isDisabledByTerraform
          ? TERRAFORM_DISABLE_YES_TOOLTIP
          : LOCKED_CHOICE_TOOLTIP
        return {
          ...option,
          disabled: isDisabled,
          label: isDisabled ? (
            <LabraToolTipV2
              description={disabledTooltip}
              tooltipPlacement="top"
            >
              <span>{option.label}</span>
            </LabraToolTipV2>
          ) : (
            option.label
          ),
        }
      }),
    [
      effectiveDisabledDataFeedsInOwnS3BucketChoices,
      currentStackType,
      isOnboardingImprovementsEnabled,
    ]
  )

  const handleThumnailClick = () => {
    setIsVideoOpen(true)
  }

  const demoVideo =
    currentStackType === 'terraform'
      ? WhatAreYouDoingHereData.cloudSettingTerraformDetails.video
      : WhatAreYouDoingHereData.cloudSettingArnDetails.video

  return (
    <BoxV2
      data-testid="create-stacks-section-box"
      heading="Create stacks"
      headingIcon={
        <div className={classes.stepHeading}>
          <CreateStacksStepStatusIcon
            section="createStacks"
            dataFeedsInOwnS3Bucket={currentDataFeedsInOwnS3Bucket}
          />
          <Typography className={classes.stepLabel}>STEP 02</Typography>
        </div>
      }
      variant="small"
      headingVariant="small"
      collapsible
      display={{ open: !!expandBox }}
      closedSecondaryActions={true}
      actions={<CreateStacksSectionActions />}
      secondaryActions={<CreateStackHelpFaqs />}
    >
      {isOnboardingImprovementsEnabled ? (
        <div className={classes.stackTypeRadioContainer}>
          <div
            className={classes.stackTypeRadioGrid}
            data-testid="stack-deployment-radio-group"
          >
            {STACK_DEPLOYMENT_RADIO_MAPPING.map(option => (
              <div key={option.key} className={classes.stackTypeRadioItem}>
                <RadioButtonsV2
                  checkedValue={stackTypeRadioCheckedValue}
                  name="stackDeploymentType"
                  radioButtonMappings={[option]}
                  handleChange={onStackTypeRadioChange}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div className={classes.dataFeedsQuestionContainer}>
        <Typography
          className={clsx(classes.sectionText)}
          style={{ marginBottom: '16px' }}
        >
          Have you already set up Data Feeds in your own S3 bucket?
        </Typography>

        <div className={classes.radioGroupWrapper}>
          <RadioButtonsV2
            checkedValue={radioCheckedValue}
            name="dataFeedsInOwnS3Bucket"
            radioButtonMappings={radioMappings}
            handleChange={onDataFeedsRadioChange}
            gapBetween="16px"
          />
        </div>
        <div>
          {isOnboardingImprovementsEnabled &&
          currentStackType === 'terraform' ? (
            <div className={classes.downloadTerraformStackRow}>
              <FontAwesomeIcon
                icon={faDownload}
                className={clsx(classes.downloadIcon, classes.download)}
              />
              <ButtonV2
                styleType="link"
                data-testid="download-terraform-stack-link"
                onClick={() => setIsTerraformUsageModalOpen(true)}
              >
                Download Terraform Stack
              </ButtonV2>
            </div>
          ) : null}
        </div>
      </div>
      {currentDataFeedsInOwnS3Bucket === 'no' ? (
        <Typography
          className={clsx(
            classes.headingSectionText,
            isOnboardingImprovementsEnabled &&
              currentStackType === 'terraform' &&
              classes.headingSectionTextTerraform
          )}
        >
          {isOnboardingImprovementsEnabled &&
          currentStackType === 'terraform' ? (
            <ColumnReadMore
              description={TERRAFORM_STACK_DESCRIPTION}
              initializeWithLowerCase
            />
          ) : (
            <ColumnReadMore
              description={CLOUDFORMATION_STACK_DESCRIPTION}
              initializeWithLowerCase
            />
          )}
        </Typography>
      ) : null}
      <>
        <div className={classes.content} style={{ marginTop: '-8px' }}>
          <div className={classes.leftColumn}>
            <div className={classes.stackSectionWrapper}>
              <div className={classes.arnSectionWrapper}>
                {currentDataFeedsInOwnS3Bucket === 'yes' ? (
                  <div>
                    <Typography className={classes.sectionHeadingPlatformRole}>
                      Platform role ARN
                    </Typography>
                    <Typography className={clsx(classes.sectionText)}>
                      In this section, you need to connect your cloud
                      environment so that you can create and manage your
                      listings via Labra.
                    </Typography>
                  </div>
                ) : null}
                {arnChildren}
              </div>
              <div
                id="flyoutCasProgramSetupWrapper"
                className={clsx(
                  classes.dataFeedsSection,
                  currentDataFeedsInOwnS3Bucket === 'yes'
                    ? classes.dataFeedsSectionMarginYes
                    : classes.dataFeedsSectionMarginNo
                )}
              >
                {currentDataFeedsInOwnS3Bucket === 'yes' ? (
                  <div>
                    <Typography className={classes.sectionHeadingDataFeeds}>
                      Data feeds
                    </Typography>
                    <Typography className={classes.subHeading}>
                      In this section you setup Data feeds, which helps labra
                      track your invoices and disbursements.
                    </Typography>
                  </div>
                ) : null}
                <div>{dataFeedsChildren}</div>
              </div>
            </div>
          </div>
          <div className={classes.verticalDivider}></div>
          <div className={classes.videoColumn}>
            <div
              className={classes.videoWrapper}
              data-testid={'vertical-divider'}
            >
              <div
                className={classes.videoContainer}
                data-testid="video-cloud-settings-details-arn"
              >
                <img
                  className={classes.thumbnailImages}
                  src={CreateStack}
                  title="Labra FlyOut"
                  alt={'what-are-you-doing-here'}
                  onClick={handleThumnailClick}
                />
                <button
                  type="button"
                  className={classes.playButton}
                  onClick={handleThumnailClick}
                  aria-label="Play video"
                >
                  <FontAwesomeIcon
                    icon={faPlay}
                    className={classes.playIcon}
                    style={{ height: '25px', width: '19px' }}
                  />
                </button>
              </div>
              <div
                data-testid="video-link-cloud-settings-arn"
                className={classes.videoLinkContainer}
              >
                <ExternalLink
                  href={WhatAreYouDoingHereData.cloudSettingArnDetails.video}
                >
                  {WhatAreYouDoingHereData.cloudSettingArnDetails.linkText}
                </ExternalLink>
              </div>
            </div>
          </div>
        </div>
        <VideoModal
          actionTitle="Platform role ARN"
          actionLable=""
          isVideoModalOpen={isVideoOpen}
          setIsVideoModalOpen={setIsVideoOpen}
          videoLink={demoVideo}
        />
        <TerraformStackUsageModal
          open={isTerraformUsageModalOpen}
          onClose={() => setIsTerraformUsageModalOpen(false)}
          awsAccountId={awsAccountId}
          awsAccountName={awsAccountName}
          clientExternalId={clientExternalId}
        />
      </>
    </BoxV2>
  )
}
